import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Layers, HelpCircle, ChevronRight, BookOpen, Link as LinkIcon, FileText, Upload, X, RotateCcw, ClipboardCheck, Activity, PlayCircle, Rss } from "lucide-react";

const DEFAULT_QUESTIONS = [
  { key: "energy", label: "Como está sua energia?", description: "0 = exausto(a) · 10 = energia ótima", min: 0, max: 10 },
  { key: "sleep", label: "Como está seu sono?", description: "0 = péssimo · 10 = restaurador", min: 0, max: 10 },
  { key: "intestine", label: "Como está seu intestino?", description: "0 = muito ruim · 10 = regulado", min: 0, max: 10 },
  { key: "adherence", label: "Qual sua adesão ao protocolo?", description: "0 = não seguiu · 10 = seguiu 100%", min: 0, max: 10 },
];

const DEFAULT_CHECKLIST_ITEMS = [
  { key: "water", label: "Hidratação (2L+)", icon: "water" },
  { key: "evacuation", label: "Evacuação", icon: "circle" },
  { key: "supplementation", label: "Suplementação", icon: "pill" },
  { key: "food", label: "Alimentação conforme plano", icon: "utensils" },
];

const DEFAULT_SYMPTOM_ITEMS = [
  { key: "gases", label: "Gases", min: 0, max: 10 },
  { key: "dor", label: "Dor abdominal", min: 0, max: 10 },
  { key: "energia", label: "Energia", min: 0, max: 10 },
  { key: "distensao", label: "Distensão", min: 0, max: 10 },
  { key: "evacuacao", label: "Qualidade evacuação", min: 0, max: 10 },
];

const ICON_OPTIONS = [
  { value: "", label: "—" },
  { value: "water", label: "💧 Água" },
  { value: "pill", label: "💊 Suplemento" },
  { value: "utensils", label: "🍴 Alimentação" },
  { value: "circle", label: "○ Círculo" },
  { value: "check", label: "✓ Check" },
];

const emptyPhase = () => ({
  id: `fase-${Math.random().toString(36).slice(2, 8)}`,
  name: "Nova fase", day_start: 1, day_end: 30,
  objectives: [], supplementation: [], expected_symptoms: "",
});

const emptyQuestion = () => ({
  key: `q_${Math.random().toString(36).slice(2, 6)}`,
  label: "Nova pergunta", description: "0 = mínimo · 10 = máximo",
  min: 0, max: 10,
});

const emptyChecklistItem = () => ({
  key: `chk_${Math.random().toString(36).slice(2, 6)}`,
  label: "Novo item do checklist", icon: "",
});

const emptySymptomItem = () => ({
  key: `sym_${Math.random().toString(36).slice(2, 6)}`,
  label: "Novo sintoma", min: 0, max: 10,
});

const emptyContentItem = () => ({
  id: `ec-${Math.random().toString(36).slice(2, 8)}`,
  type: "article", title: "Novo conteúdo", url: "", duration: "",
});

const emptyEducation = () => ({ html: "", links: [], pdfs: [], items: [] });

const listToText = (arr) => (arr || []).join("\n");
const textToList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

const isValidUrl = (u) => {
  try { const p = new URL(u); return p.protocol === "http:" || p.protocol === "https:"; }
  catch { return false; }
};

export function validateProtocol(p) {
  if (!p.name || p.name.trim().length < 2) return "Nome é obrigatório (mín. 2 caracteres).";
  if (!p.category || p.category.trim().length < 2) return "Categoria é obrigatória.";
  if (!p.description || p.description.trim().length < 2) return "Descrição é obrigatória.";
  if (!p.duration_days || p.duration_days < 1) return "Duração deve ser >= 1 dia.";
  if (!p.phases || p.phases.length === 0) return "Adicione ao menos uma fase.";
  for (const ph of p.phases) {
    if (!ph.name) return "Todas as fases precisam de nome.";
    if (ph.day_end < ph.day_start) return `Fase "${ph.name}": dia final deve ser >= inicial.`;
  }
  const keys = (p.checkin_questions || []).map((q) => q.key);
  if (new Set(keys).size !== keys.length) return "Chaves de perguntas devem ser únicas.";
  for (const q of p.checkin_questions || []) {
    if (!q.key || !q.label) return "Todas as perguntas precisam de chave e rótulo.";
  }
  const cKeys = (p.checklist_items || []).map((c) => c.key);
  if (new Set(cKeys).size !== cKeys.length) return "Chaves do checklist devem ser únicas.";
  for (const c of p.checklist_items || []) {
    if (!c.key || !c.label) return "Todos os itens do checklist precisam de chave e rótulo.";
  }
  const sKeys = (p.symptom_items || []).map((s) => s.key);
  if (new Set(sKeys).size !== sKeys.length) return "Chaves dos sintomas devem ser únicas.";
  for (const s of p.symptom_items || []) {
    if (!s.key || !s.label) return "Todos os sintomas precisam de chave e rótulo.";
  }
  const edu = p.education || {};
  for (const l of edu.links || []) {
    if (!l.title || !l.url) return "Preencha título e URL de todos os links.";
    if (!isValidUrl(l.url)) return `URL inválida: ${l.url}`;
  }
  for (const it of edu.items || []) {
    if (!it.title || !it.url) return "Preencha título e URL de todos os cards de conteúdo.";
    if (!isValidUrl(it.url)) return `URL de card inválida: ${it.url}`;
    if (!["video", "pdf", "article"].includes(it.type)) return `Tipo inválido no card "${it.title}".`;
  }
  return null;
}

export function normalizeProtocolForForm(p) {
  return {
    ...p,
    indications: p.indications || [],
    phases: (p.phases || []).map((ph) => ({
      ...ph, objectives: ph.objectives || [], supplementation: ph.supplementation || [],
    })),
    checkin_questions: p.checkin_questions && p.checkin_questions.length > 0 ? p.checkin_questions : [...DEFAULT_QUESTIONS],
    checklist_items: p.checklist_items && p.checklist_items.length > 0 ? p.checklist_items : [...DEFAULT_CHECKLIST_ITEMS],
    symptom_items: p.symptom_items && p.symptom_items.length > 0 ? p.symptom_items : [...DEFAULT_SYMPTOM_ITEMS],
    education: { ...emptyEducation(), ...(p.education || {}) },
  };
}

/**
 * Reusable protocol editor body.
 * Props:
 *  - value: protocol object
 *  - onChange: fn(newProtocol)
 *  - showActiveToggle: bool (only for master template editor)
 */
export default function ProtocolEditorPanel({ value, onChange, showActiveToggle = false }) {
  const [tab, setTab] = useState("basic");
  const p = value;
  const edu = p.education || emptyEducation();

  const setP = (patch) => onChange({ ...p, ...patch });
  const setEdu = (patch) => setP({ education: { ...edu, ...patch } });

  // Phase helpers
  const updatePhase = (idx, patch) => setP({ phases: p.phases.map((ph, i) => (i === idx ? { ...ph, ...patch } : ph)) });
  const addPhase = () => {
    const last = p.phases[p.phases.length - 1];
    const start = last ? Number(last.day_end) + 1 : 1;
    setP({ phases: [...p.phases, { ...emptyPhase(), day_start: start, day_end: start + 29 }] });
  };
  const removePhase = (idx) => {
    if (p.phases.length <= 1) { toast.error("Precisa de pelo menos uma fase."); return; }
    setP({ phases: p.phases.filter((_, i) => i !== idx) });
  };

  // Question helpers
  const updateQ = (idx, patch) => setP({ checkin_questions: p.checkin_questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)) });
  const addQ = () => setP({ checkin_questions: [...p.checkin_questions, emptyQuestion()] });
  const removeQ = (idx) => setP({ checkin_questions: p.checkin_questions.filter((_, i) => i !== idx) });
  const resetQ = () => setP({ checkin_questions: [...DEFAULT_QUESTIONS] });

  // Checklist item helpers
  const checklistItems = p.checklist_items || [];
  const updateChk = (idx, patch) => setP({ checklist_items: checklistItems.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  const addChk = () => setP({ checklist_items: [...checklistItems, emptyChecklistItem()] });
  const removeChk = (idx) => setP({ checklist_items: checklistItems.filter((_, i) => i !== idx) });
  const resetChk = () => setP({ checklist_items: [...DEFAULT_CHECKLIST_ITEMS] });

  // Symptom item helpers
  const symptomItems = p.symptom_items || [];
  const updateSym = (idx, patch) => setP({ symptom_items: symptomItems.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
  const addSym = () => setP({ symptom_items: [...symptomItems, emptySymptomItem()] });
  const removeSym = (idx) => setP({ symptom_items: symptomItems.filter((_, i) => i !== idx) });
  const resetSym = () => setP({ symptom_items: [...DEFAULT_SYMPTOM_ITEMS] });

  // Education helpers
  const addLink = () => setEdu({ links: [...(edu.links || []), { title: "", url: "" }] });
  const updateLink = (idx, patch) => setEdu({ links: edu.links.map((l, i) => (i === idx ? { ...l, ...patch } : l)) });
  const removeLink = (idx) => setEdu({ links: edu.links.filter((_, i) => i !== idx) });

  // Educational content cards (items) helpers
  const contentItems = edu.items || [];
  const addContentItem = () => setEdu({ items: [...contentItems, emptyContentItem()] });
  const updateContentItem = (idx, patch) => setEdu({ items: contentItems.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  const removeContentItem = (idx) => setEdu({ items: contentItems.filter((_, i) => i !== idx) });

  const [uploading, setUploading] = useState(false);
  const onUploadPdf = async (e, presetTitle = "") => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Envie apenas arquivos PDF."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Arquivo maior que 15 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/uploads/protocol-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const title = presetTitle || file.name.replace(/\.pdf$/i, "");
      setEdu({ pdfs: [...(edu.pdfs || []), { title, url: data.url, filename: data.filename }] });
      toast.success("PDF adicionado.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao enviar PDF.");
    } finally { setUploading(false); }
  };
  const updatePdf = (idx, patch) => setEdu({ pdfs: edu.pdfs.map((x, i) => (i === idx ? { ...x, ...patch } : x)) });
  const removePdf = (idx) => setEdu({ pdfs: edu.pdfs.filter((_, i) => i !== idx) });

  return (
    <>
      {/* tabs */}
      <div className="border-b border-border px-6 flex gap-1 flex-wrap">
        {[
          { k: "basic", label: "Básico", icon: ChevronRight },
          { k: "phases", label: `Fases (${p.phases.length})`, icon: Layers },
          { k: "checklist", label: `Checklist (${checklistItems.length})`, icon: ClipboardCheck },
          { k: "symptoms", label: `Sintomas (${symptomItems.length})`, icon: Activity },
          { k: "questions", label: `Check-in (${p.checkin_questions.length})`, icon: HelpCircle },
          { k: "education", label: `Educativo (${contentItems.length + (edu.links?.length || 0) + (edu.pdfs?.length || 0)})`, icon: BookOpen },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`protocol-tab-${t.k}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-6 overflow-y-auto flex-1 space-y-4">
        {tab === "basic" && (
          <div className="space-y-4">
            <div>
              <label className="overline text-muted-foreground mb-1 block">Nome</label>
              <input value={p.name} onChange={(e) => setP({ name: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="protocol-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground mb-1 block">Categoria</label>
                <input value={p.category} onChange={(e) => setP({ category: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="protocol-category-input" />
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Duração (dias)</label>
                <input type="number" min={1} max={720} value={p.duration_days}
                  onChange={(e) => setP({ duration_days: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="protocol-duration-input" />
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Descrição</label>
              <textarea rows={3} value={p.description}
                onChange={(e) => setP({ description: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="protocol-description-input" />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Indicações (uma por linha)</label>
              <textarea rows={3} value={listToText(p.indications)}
                onChange={(e) => setP({ indications: textToList(e.target.value) })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="protocol-indications-input" />
            </div>
            {showActiveToggle && p.id && (
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={p.active !== false}
                  onChange={(e) => setP({ active: e.target.checked })}
                  className="accent-primary w-4 h-4" data-testid="protocol-active-checkbox" />
                <span className="text-sm">Protocolo ativo (visível para pacientes)</span>
              </label>
            )}
          </div>
        )}

        {tab === "phases" && (
          <div className="space-y-4">
            {p.phases.map((ph, idx) => (
              <div key={ph.id} className="border border-border rounded-sm p-4 space-y-3" data-testid={`phase-editor-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <label className="overline text-muted-foreground mb-1 block">Nome da fase</label>
                    <input value={ph.name} onChange={(e) => updatePhase(idx, { name: e.target.value })}
                      className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`phase-name-${idx}`} />
                  </div>
                  <button onClick={() => removePhase(idx)} className="p-2 rounded-sm hover:bg-muted transition-colors mt-6" data-testid={`remove-phase-${idx}`} title="Remover fase">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-muted-foreground mb-1 block">Dia inicial</label>
                    <input type="number" min={1} value={ph.day_start}
                      onChange={(e) => updatePhase(idx, { day_start: Number(e.target.value) })}
                      className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`phase-day-start-${idx}`} />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground mb-1 block">Dia final</label>
                    <input type="number" min={1} value={ph.day_end}
                      onChange={(e) => updatePhase(idx, { day_end: Number(e.target.value) })}
                      className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`phase-day-end-${idx}`} />
                  </div>
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Objetivos (um por linha)</label>
                  <textarea rows={3} value={listToText(ph.objectives)}
                    onChange={(e) => updatePhase(idx, { objectives: textToList(e.target.value) })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`phase-objectives-${idx}`} />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Suplementação (uma por linha)</label>
                  <textarea rows={3} value={listToText(ph.supplementation)}
                    onChange={(e) => updatePhase(idx, { supplementation: textToList(e.target.value) })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`phase-supplementation-${idx}`} />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Sintomas esperados</label>
                  <textarea rows={2} value={ph.expected_symptoms || ""}
                    onChange={(e) => updatePhase(idx, { expected_symptoms: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`phase-symptoms-${idx}`} />
                </div>
              </div>
            ))}
            <button onClick={addPhase} className="inline-flex items-center gap-2 rounded-sm border border-dashed border-primary/40 text-primary px-4 py-2 text-sm font-semibold hover:bg-primary/5 transition-colors" data-testid="add-phase-btn">
              <Plus className="w-4 h-4" /> Adicionar fase
            </button>
          </div>
        )}

        {tab === "checklist" && (
          <div className="space-y-4">
            <div className="rounded-sm bg-muted/40 border border-border p-3 text-xs text-muted-foreground">
              Itens do <b>Checklist Diário</b> — paciente marca todo dia (Hidratação, Suplementação, etc). Chave é usada internamente; o rótulo é o que o paciente vê.
              <button onClick={resetChk} className="ml-2 underline text-primary" data-testid="reset-checklist-btn">Restaurar padrão</button>
            </div>
            {checklistItems.map((c, idx) => (
              <div key={idx} className="border border-border rounded-sm p-4 space-y-3" data-testid={`checklist-editor-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_150px] gap-3 flex-1">
                    <div>
                      <label className="overline text-muted-foreground mb-1 block">Chave</label>
                      <input value={c.key} onChange={(e) => updateChk(idx, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })}
                        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm font-mono" data-testid={`checklist-key-${idx}`} />
                    </div>
                    <div>
                      <label className="overline text-muted-foreground mb-1 block">Rótulo (aparece para o paciente)</label>
                      <input value={c.label} onChange={(e) => updateChk(idx, { label: e.target.value })}
                        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`checklist-label-${idx}`} />
                    </div>
                    <div>
                      <label className="overline text-muted-foreground mb-1 block">Ícone</label>
                      <select value={c.icon || ""} onChange={(e) => updateChk(idx, { icon: e.target.value })}
                        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`checklist-icon-${idx}`}>
                        {ICON_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <button onClick={() => removeChk(idx)} className="p-2 rounded-sm hover:bg-muted transition-colors mt-6" data-testid={`remove-checklist-${idx}`} title="Remover item">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              </div>
            ))}
            <button onClick={addChk} className="inline-flex items-center gap-2 rounded-sm border border-dashed border-primary/40 text-primary px-4 py-2 text-sm font-semibold hover:bg-primary/5 transition-colors" data-testid="add-checklist-btn">
              <Plus className="w-4 h-4" /> Adicionar item ao checklist
            </button>
          </div>
        )}

        {tab === "symptoms" && (
          <div className="space-y-4">
            <div className="rounded-sm bg-muted/40 border border-border p-3 text-xs text-muted-foreground">
              Sintomas monitorados pelo paciente diariamente (slider 0–10). São diferentes das perguntas de check-in semanal.
              <button onClick={resetSym} className="ml-2 underline text-primary" data-testid="reset-symptoms-btn">Restaurar padrão</button>
            </div>
            {symptomItems.map((s, idx) => (
              <div key={idx} className="border border-border rounded-sm p-4 space-y-3" data-testid={`symptom-editor-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
                    <div>
                      <label className="overline text-muted-foreground mb-1 block">Chave</label>
                      <input value={s.key} onChange={(e) => updateSym(idx, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })}
                        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm font-mono" data-testid={`symptom-key-${idx}`} />
                    </div>
                    <div>
                      <label className="overline text-muted-foreground mb-1 block">Rótulo</label>
                      <input value={s.label} onChange={(e) => updateSym(idx, { label: e.target.value })}
                        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`symptom-label-${idx}`} />
                    </div>
                  </div>
                  <button onClick={() => removeSym(idx)} className="p-2 rounded-sm hover:bg-muted transition-colors mt-6" data-testid={`remove-symptom-${idx}`} title="Remover sintoma">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" value={s.min ?? 0} onChange={(e) => updateSym(idx, { min: Number(e.target.value) })} placeholder="Mín"
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`symptom-min-${idx}`} />
                  <input type="number" value={s.max ?? 10} onChange={(e) => updateSym(idx, { max: Number(e.target.value) })} placeholder="Máx"
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`symptom-max-${idx}`} />
                </div>
              </div>
            ))}
            <button onClick={addSym} className="inline-flex items-center gap-2 rounded-sm border border-dashed border-primary/40 text-primary px-4 py-2 text-sm font-semibold hover:bg-primary/5 transition-colors" data-testid="add-symptom-btn">
              <Plus className="w-4 h-4" /> Adicionar sintoma
            </button>
          </div>
        )}

        {tab === "questions" && (
          <div className="space-y-4">
            <div className="rounded-sm bg-muted/40 border border-border p-3 text-xs text-muted-foreground">
              As perguntas aparecerão no check-in semanal.
              <button onClick={resetQ} className="ml-2 underline text-primary" data-testid="reset-questions-btn">Restaurar padrão</button>
            </div>
            {p.checkin_questions.map((q, idx) => (
              <div key={idx} className="border border-border rounded-sm p-4 space-y-3" data-testid={`question-editor-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="grid grid-cols-2 gap-3 flex-1">
                    <div>
                      <label className="overline text-muted-foreground mb-1 block">Chave</label>
                      <input value={q.key} onChange={(e) => updateQ(idx, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })}
                        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm font-mono" data-testid={`question-key-${idx}`} />
                    </div>
                    <div>
                      <label className="overline text-muted-foreground mb-1 block">Rótulo</label>
                      <input value={q.label} onChange={(e) => updateQ(idx, { label: e.target.value })}
                        className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`question-label-${idx}`} />
                    </div>
                  </div>
                  <button onClick={() => removeQ(idx)} className="p-2 rounded-sm hover:bg-muted transition-colors mt-6" data-testid={`remove-question-${idx}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </button>
                </div>
                <input value={q.description || ""} onChange={(e) => updateQ(idx, { description: e.target.value })}
                  placeholder="Descrição/instrução" className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`question-desc-${idx}`} />
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" value={q.min ?? 0} onChange={(e) => updateQ(idx, { min: Number(e.target.value) })} placeholder="Mín"
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`question-min-${idx}`} />
                  <input type="number" value={q.max ?? 10} onChange={(e) => updateQ(idx, { max: Number(e.target.value) })} placeholder="Máx"
                    className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid={`question-max-${idx}`} />
                </div>
              </div>
            ))}
            <button onClick={addQ} className="inline-flex items-center gap-2 rounded-sm border border-dashed border-primary/40 text-primary px-4 py-2 text-sm font-semibold hover:bg-primary/5 transition-colors" data-testid="add-question-btn">
              <Plus className="w-4 h-4" /> Adicionar pergunta
            </button>
          </div>
        )}

        {tab === "education" && (
          <div className="space-y-6">
            {/* Cards de conteúdo (video/pdf/article) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-gold inline-flex items-center gap-2"><PlayCircle className="w-3.5 h-3.5" /> Cards de conteúdo (vídeos, PDFs, artigos)</div>
                <button onClick={addContentItem} className="inline-flex items-center gap-1 rounded-sm border border-primary/40 text-primary px-3 py-1.5 text-xs font-semibold hover:bg-primary/5 transition-colors" data-testid="add-content-item-btn">
                  <Plus className="w-3 h-3" /> Adicionar card
                </button>
              </div>
              <div className="text-xs text-muted-foreground mb-2">Aparecem para o paciente na seção &quot;Conteúdo Educativo&quot; dentro do protocolo ativo.</div>
              {contentItems.length === 0 && <div className="text-xs text-muted-foreground">Nenhum card adicionado.</div>}
              <div className="space-y-2">
                {contentItems.map((c, idx) => (
                  <div key={c.id || idx} className="border border-border rounded-sm p-3 space-y-2" data-testid={`content-item-row-${idx}`}>
                    <div className="grid grid-cols-1 md:grid-cols-[120px_1fr_100px_auto] gap-2 items-center">
                      <select value={c.type} onChange={(e) => updateContentItem(idx, { type: e.target.value })}
                        className="px-3 py-1.5 rounded-sm border border-input bg-card text-sm" data-testid={`content-type-${idx}`}>
                        <option value="video">Vídeo</option>
                        <option value="pdf">PDF</option>
                        <option value="article">Artigo</option>
                      </select>
                      <input value={c.title} onChange={(e) => updateContentItem(idx, { title: e.target.value })} placeholder="Título"
                        className="px-3 py-1.5 rounded-sm border border-input bg-card text-sm" data-testid={`content-title-${idx}`} />
                      <input value={c.duration || ""} onChange={(e) => updateContentItem(idx, { duration: e.target.value })} placeholder="Duração (opc.)"
                        className="px-3 py-1.5 rounded-sm border border-input bg-card text-sm" data-testid={`content-duration-${idx}`} />
                      <button onClick={() => removeContentItem(idx)} className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid={`remove-content-item-${idx}`}>
                        <X className="w-4 h-4 text-destructive" />
                      </button>
                    </div>
                    <input value={c.url} onChange={(e) => updateContentItem(idx, { url: e.target.value })} placeholder="https://…"
                      className="w-full px-3 py-1.5 rounded-sm border border-input bg-card text-sm font-mono" data-testid={`content-url-${idx}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Rich text */}
            <div>
              <div className="overline text-gold mb-2">Conteúdo educativo (texto livre)</div>
              <textarea rows={8} value={edu.html || ""}
                onChange={(e) => setEdu({ html: e.target.value })}
                placeholder="Escreva orientações, explicações e instruções educativas… Aceita quebras de linha simples."
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm font-body"
                data-testid="education-html-input" />
              <div className="text-xs text-muted-foreground mt-1">Até 20 000 caracteres. Texto simples (sem formatação avançada).</div>
            </div>

            {/* Links */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-gold inline-flex items-center gap-2"><LinkIcon className="w-3.5 h-3.5" /> Links externos</div>
                <button onClick={addLink} className="inline-flex items-center gap-1 rounded-sm border border-primary/40 text-primary px-3 py-1.5 text-xs font-semibold hover:bg-primary/5 transition-colors" data-testid="add-link-btn">
                  <Plus className="w-3 h-3" /> Adicionar link
                </button>
              </div>
              {(edu.links || []).length === 0 && <div className="text-xs text-muted-foreground">Nenhum link adicionado.</div>}
              <div className="space-y-2">
                {(edu.links || []).map((l, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-2 items-center border border-border rounded-sm p-2" data-testid={`link-row-${idx}`}>
                    <input value={l.title} onChange={(e) => updateLink(idx, { title: e.target.value })} placeholder="Título"
                      className="px-3 py-1.5 rounded-sm border border-input bg-card text-sm" data-testid={`link-title-${idx}`} />
                    <input value={l.url} onChange={(e) => updateLink(idx, { url: e.target.value })} placeholder="https://…"
                      className="px-3 py-1.5 rounded-sm border border-input bg-card text-sm font-mono" data-testid={`link-url-${idx}`} />
                    <button onClick={() => removeLink(idx)} className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid={`remove-link-${idx}`}>
                      <X className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* PDFs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-gold inline-flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> PDFs</div>
                <label className={`inline-flex items-center gap-1 rounded-sm border border-primary/40 text-primary px-3 py-1.5 text-xs font-semibold cursor-pointer hover:bg-primary/5 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="upload-pdf-btn">
                  <Upload className="w-3 h-3" /> {uploading ? "Enviando…" : "Enviar PDF"}
                  <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => onUploadPdf(e)} data-testid="upload-pdf-input" />
                </label>
              </div>
              {(edu.pdfs || []).length === 0 && <div className="text-xs text-muted-foreground">Nenhum PDF adicionado.</div>}
              <div className="space-y-2">
                {(edu.pdfs || []).map((pdf, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto] gap-2 items-center border border-border rounded-sm p-2" data-testid={`pdf-row-${idx}`}>
                    <input value={pdf.title} onChange={(e) => updatePdf(idx, { title: e.target.value })} placeholder="Título exibido"
                      className="px-3 py-1.5 rounded-sm border border-input bg-card text-sm" data-testid={`pdf-title-${idx}`} />
                    <div className="text-xs text-muted-foreground font-mono truncate" data-testid={`pdf-filename-${idx}`}>{pdf.filename || pdf.url}</div>
                    <button onClick={() => removePdf(idx)} className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid={`remove-pdf-${idx}`}>
                      <X className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
